/**
 * Domínio de Oportunidades compartilhado entre servidor e telas (portado da
 * DEMO-03: dados.ts, busca.ts, periodo.ts, eventos): estágios, tipos, alerta
 * PROGRESSIVO do Registro da Oportunidade (faixas parametrizadas, P-15),
 * situação das tarefas, prioridade por data, BUSCA CRUZADA por identificador
 * e filtros de período. "Hoje" vem sempre de fora (relógio do servidor,
 * America/Sao_Paulo) — nunca uma data fixa.
 */

export type Tipo = 'venda' | 'locacao' | 'trade_in';
export type Estagio = 'prospeccao' | 'qualificacao' | 'proposta_em_elaboracao' | 'proposta_enviada' | 'negociacao' | 'ganha' | 'ganha_parcialmente' | 'perdida' | 'cancelada';
export const TIPOS: Record<Tipo, string> = { venda: 'Venda', locacao: 'Locação', trade_in: 'Trade-in' };
export const ESTAGIOS: { id: Estagio; nome: string; aberto: boolean }[] = [
  { id: 'prospeccao', nome: 'Prospecção', aberto: true },
  { id: 'qualificacao', nome: 'Qualificação', aberto: true },
  { id: 'proposta_em_elaboracao', nome: 'Em elaboração', aberto: true }, // P4 bloco 3 (D5): nome de apresentação; o id não muda
  { id: 'proposta_enviada', nome: 'Enviada', aberto: true },
  { id: 'negociacao', nome: 'Negociação', aberto: true },
  { id: 'ganha', nome: 'Ganha', aberto: false },
  { id: 'ganha_parcialmente', nome: 'Ganha parcialmente', aberto: false },
  { id: 'perdida', nome: 'Perdida', aberto: false },
  { id: 'cancelada', nome: 'Cancelada', aberto: false },
];
export const ESTAGIOS_ABERTOS = ESTAGIOS.filter((e) => e.aberto).map((e) => e.id);
export const nomeEstagio = (e: Estagio) => ESTAGIOS.find((x) => x.id === e)!.nome;
export const estagioAberto = (e: Estagio) => ESTAGIOS.find((x) => x.id === e)!.aberto;
/** Estágios só avançam automaticamente (ex.: criar proposta leva a "Proposta em elaboração"); manualmente, qualquer estágio aberto. */
export function avancarEstagio(atual: Estagio, alvo: Estagio): Estagio {
  const ordem: Estagio[] = ['prospeccao', 'qualificacao', 'proposta_em_elaboracao', 'proposta_enviada', 'negociacao'];
  const i = ordem.indexOf(atual);
  return i >= 0 && i < ordem.indexOf(alvo) ? alvo : atual;
}

/* ---------- datas ---------- */
export const dataBr = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
export const diasEntre = (de: string, ate: string) => Math.round((Date.parse(`${ate}T00:00:00Z`) - Date.parse(`${de}T00:00:00Z`)) / 86_400_000);
export const somarDias = (s: string, n: number) => new Date(Date.parse(`${s}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/* ---------- Registro da Oportunidade: alerta PROGRESSIVO ---------- */
export type NivelRegistro = 'valido' | 'aviso' | 'atencao' | 'urgente' | 'critico' | 'substituido' | 'encerrado';
export const NIVEIS: Record<NivelRegistro, string> = { valido: 'Válido', aviso: 'Aviso', atencao: 'Atenção', urgente: 'Urgente', critico: 'Crítico', substituido: 'Substituído', encerrado: 'Encerrado' };
/** Faixas em dias [aviso, atenção, urgente] (P-15; a DEMO-03 usou 30, 15 e 7). */
export type FaixasRegistro = [number, number, number];
export type SituacaoRegistro = 'ativo' | 'renovado' | 'substituido' | 'encerrado';
export interface RegistroBase {
  numero: string;
  data_vencimento: string;
  situacao: SituacaoRegistro;
  substituido_por?: string | null;
  /** Pacote 5 (0052): encerramento do registro (não alerta mais; só a reabertura o devolve a "ativo"). */
  encerrado_em?: string | Date | null;
  motivo_encerramento?: string | null;
}
/**
 * Pacote 5: motivos do encerramento do Registro (lista fixa do HUB). "Outro" exige observação.
 * Encerrar não muda o estágio da Oportunidade.
 */
export const MOTIVOS_ENCERRAMENTO_REGISTRO = ['Fabricante não renovou', 'Projeto adiado pelo Cliente', 'Oportunidade seguirá sem registro', 'Registro feito por outro parceiro', 'Outro'] as const;
export type MotivoEncerramentoRegistro = (typeof MOTIVOS_ENCERRAMENTO_REGISTRO)[number];
export const motivoEncerramentoExigeObservacao = (m: string) => m === 'Outro';
/** Data (dd/mm/aaaa, America/Sao_Paulo) do encerramento do registro. */
export const dataEncerramentoRegistro = (v: string | Date | null | undefined) => (v ? new Date(v).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
/** Registro fora do acompanhamento (substituído ou encerrado): não alerta, não entra nos lembretes nem no resumo. */
export const registroForaDeAcompanhamento = (r: Pick<RegistroBase, 'situacao'>) => r.situacao === 'substituido' || r.situacao === 'encerrado';
export function statusRegistro(r: RegistroBase, hoje: string, faixas: FaixasRegistro): { nivel: NivelRegistro; texto: string; dias: number } {
  const dias = diasEntre(hoje, r.data_vencimento);
  if (r.situacao === 'substituido') return { nivel: 'substituido', texto: `Substituído por ${r.substituido_por}`, dias };
  if (r.situacao === 'encerrado') return { nivel: 'encerrado', texto: `Encerrado em ${dataEncerramentoRegistro(r.encerrado_em)}${r.motivo_encerramento ? ` · ${r.motivo_encerramento}` : ''}`, dias };
  const [aviso, atencao, urgente] = faixas;
  const nivel: NivelRegistro = dias < 0 ? 'critico' : dias <= urgente ? 'urgente' : dias <= atencao ? 'atencao' : dias <= aviso ? 'aviso' : 'valido';
  const quando = dias < 0 ? `vencido há ${-dias} dia(s)` : `vence em ${dias} dia(s)`;
  return { nivel, texto: `${NIVEIS[nivel]} — ${quando}${r.situacao === 'renovado' ? ' (renovado)' : ''}`, dias };
}
export const alertaAtivo = (r: RegistroBase, hoje: string, faixas: FaixasRegistro) => ['aviso', 'atencao', 'urgente', 'critico'].includes(statusRegistro(r, hoje, faixas).nivel);
/** Registro "principal" para exibição resumida (o acompanhado — nem substituído nem encerrado — que vence primeiro). */
export const registroPrincipal = <R extends RegistroBase>(rs: R[]) => rs.filter((r) => !registroForaDeAcompanhamento(r)).sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento))[0];

/* ---------- Tarefas e prioridades por data ---------- */
export type SituacaoTarefa = 'vencidas' | 'hoje' | 'proximas' | 'concluidas' | 'abertas' | 'todas';
export const situacaoTarefa = (t: { data: string; concluida: boolean }, hoje: string): Exclude<SituacaoTarefa, 'abertas' | 'todas'> => (t.concluida ? 'concluidas' : t.data < hoje ? 'vencidas' : t.data === hoje ? 'hoje' : 'proximas');
/** Prioridade de uma ação pela data (Início): vencida crítica, hoje urgente, até 3 dias atenção, até 7 aviso. */
export const nivelPorData = (data: string, hoje: string, antecedenciaAviso = 7): NivelRegistro => (data < hoje ? 'critico' : data === hoje ? 'urgente' : data <= somarDias(hoje, 3) ? 'atencao' : data <= somarDias(hoje, antecedenciaAviso) ? 'aviso' : 'valido');
export const PESO_NIVEL: Record<NivelRegistro, number> = { critico: 0, urgente: 1, atencao: 2, aviso: 3, valido: 4, substituido: 5, encerrado: 6 };

/* ---------- Busca cruzada por identificador ---------- */
export type TipoChave = 'pedido' | 'oportunidade' | 'proposta' | 'ocf' | 'ol' | 'oti' | 'oc' | 'nf' | 'texto' | 'cnpj' | 'legado';
export interface Chave {
  tipo: TipoChave;
  valor: string; // como é exibido (ex.: "OCF-000908")
  /**
   * Identificador LEGADO (planilha): `de` diz em que formato ele é digitado — "PED-021" é consultado como PED- mas é a
   * compra ao parceiro (OCF), nunca um Pedido do HUB (PED-000022+); `nota` explica o que ele era.
   */
  de?: TipoChave;
  nota?: string;
}
export interface Resultado<T> {
  item: T;
  motivo: string; // ex.: "OCF-000908"
  chave: Chave;
}

const semAcento = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const digitos = (s: string) => s.replace(/\D/g, '');
const numero = (s: string) => {
  const g = digitos(s);
  return g ? String(Number(g)) : '';
};

/** Interpreta a consulta: prefixo (tipo) + parte numérica, ou texto livre. */
export function interpretar(consulta: string): { vazia: boolean; tipo?: TipoChave; num?: string; texto: string } {
  const q = semAcento(consulta);
  if (!q) return { vazia: true, texto: '' };
  const pref = q.match(/^(ped|ocf|oti|ol|oc|nf|#|op)[\s-]*(\d[\d./-]*)$/);
  if (pref) {
    const mapa: Record<string, TipoChave> = { ped: 'pedido', ocf: 'ocf', oti: 'oti', ol: 'ol', oc: 'oc', nf: 'nf', '#': 'oportunidade', op: 'oportunidade' };
    return { vazia: false, tipo: mapa[pref[1]], num: numero(pref[2]), texto: q };
  }
  if (/^\d{1,4}\/\d{4}$/.test(q)) return { vazia: false, tipo: 'proposta', num: numero(q.split('/')[0]), texto: q };
  if (/^[\d\s./-]+$/.test(q)) return { vazia: false, num: numero(q), texto: q };
  return { vazia: false, texto: q };
}

const ID_NUMERICO: TipoChave[] = ['pedido', 'oportunidade', 'proposta', 'ocf', 'ol', 'oti', 'nf'];

/** Verifica se uma chave casa com a consulta. */
function casa(c: Chave, i: ReturnType<typeof interpretar>): boolean {
  if (c.tipo === 'legado') {
    const n = c.de === 'proposta' ? numero(c.valor.split('/')[0]) : numero(c.valor);
    if (i.tipo) return c.de === i.tipo && n === i.num;
    return i.num !== undefined && n === i.num;
  }
  if (i.tipo) {
    if (c.tipo !== i.tipo) return false;
    if (c.tipo === 'oc') return digitos(c.valor).includes(digitos(i.texto)) && digitos(i.texto).length > 0;
    const n = c.tipo === 'proposta' ? numero(c.valor.split('/')[0]) : numero(c.valor);
    return n === i.num;
  }
  if (i.num !== undefined && ID_NUMERICO.includes(c.tipo)) {
    const n = c.tipo === 'proposta' ? numero(c.valor.split('/')[0]) : numero(c.valor);
    return n === i.num;
  }
  if (c.tipo === 'cnpj') return digitos(i.texto).length >= 5 && digitos(c.valor).includes(digitos(i.texto));
  if (c.tipo === 'oc') return semAcento(c.valor).includes(i.texto) || (digitos(i.texto).length >= 3 && digitos(c.valor).includes(digitos(i.texto)));
  if (c.tipo === 'texto') return i.num === undefined && semAcento(c.valor).includes(i.texto);
  return false;
}

/** Prioridade do motivo exibido (o identificador mais específico primeiro). */
const ORDEM: TipoChave[] = ['ocf', 'ol', 'oti', 'pedido', 'proposta', 'oportunidade', 'legado', 'oc', 'cnpj', 'texto'];

export function buscar<T>(itens: T[], chaves: (t: T) => Chave[], consulta: string): Resultado<T>[] | null {
  const i = interpretar(consulta);
  if (i.vazia) return null;
  const r: Resultado<T>[] = [];
  for (const item of itens) {
    const ok = chaves(item)
      .filter((c) => casa(c, i))
      .sort((a, b) => ORDEM.indexOf(a.tipo) - ORDEM.indexOf(b.tipo));
    if (ok.length) r.push({ item, motivo: ok[0].valor, chave: ok[0] });
  }
  return r;
}



/** Frase do motivo de cada resultado da busca ("Encontrada pela Proposta 0231/2026"). */
export function textoMotivo(r: Resultado<unknown>, alvo: 'pedido' | 'ordem' | 'oportunidade'): string {
  const tipo = r.chave.tipo;
  if (tipo === 'legado') return `${alvo === 'pedido' ? 'Encontrado' : 'Encontrada'} pelo nº legado ${r.motivo}${r.chave.nota ? ` (${r.chave.nota})` : ''}`;
  const verbo = alvo === 'oportunidade' ? 'Encontrada' : 'Encontrado';
  if (tipo === 'ocf' || tipo === 'ol' || tipo === 'oti') return alvo === 'oportunidade' ? `${verbo} pela ${r.motivo}` : `${verbo} pela Ordem ${r.motivo}`;
  if (tipo === 'pedido') return `${verbo} pelo ${r.motivo}`;
  if (tipo === 'proposta') return `${verbo} pela Proposta ${r.motivo}`;
  if (tipo === 'oportunidade') return `${verbo} pela Oportunidade ${r.motivo}`;
  if (tipo === 'oc') return `${verbo} pela OC do Cliente ${r.motivo}`;
  if (tipo === 'nf') return `${verbo} pela ${r.motivo} (faturamento)`;
  if (tipo === 'cnpj') return `${verbo} pelo CNPJ ${r.motivo}`;
  return `${verbo} por “${r.motivo}”`;
}

/* ---------- Filtros de período (cada tela diz QUAL data está sendo filtrada) ---------- */
export type Preset = 'tudo' | 'semana' | 'mes' | 'mes_passado' | '30' | '90' | 'trimestre' | 'ano' | 'personalizado';
export interface Periodo {
  preset: Preset;
  trimestre: 1 | 2 | 3 | 4;
  ano: number;
  de: string;
  ate: string;
}
export const PRESETS: { id: Preset; rotulo: string }[] = [
  { id: 'tudo', rotulo: 'Todo o período' },
  { id: 'semana', rotulo: 'Esta semana' },
  { id: 'mes', rotulo: 'Este mês' },
  { id: 'mes_passado', rotulo: 'Mês passado' },
  { id: '30', rotulo: 'Últimos 30 dias' },
  { id: '90', rotulo: 'Últimos 90 dias' },
  { id: 'trimestre', rotulo: 'Trimestre' },
  { id: 'ano', rotulo: 'Ano' },
  { id: 'personalizado', rotulo: 'Período personalizado' },
];
export const periodoInicial = (hoje: string): Periodo => ({ preset: 'tudo', trimestre: (Math.floor((Number(hoje.slice(5, 7)) - 1) / 3) + 1) as Periodo['trimestre'], ano: Number(hoje.slice(0, 4)), de: `${hoje.slice(0, 8)}01`, ate: hoje });

const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const dia = (s: string) => Date.parse(`${s}T00:00:00Z`);
const somar = (s: string, dias: number) => iso(dia(s) + dias * 86_400_000);

/** Intervalo inclusivo [de, ate] do período e o rótulo explícito. */
export function intervalo(p: Periodo, HOJE: string): { de: string | null; ate: string | null; rotulo: string } {
  switch (p.preset) {
    case 'tudo':
      return { de: null, ate: null, rotulo: 'Todo o período' };
    case 'semana': {
      const semana = new Date(dia(HOJE)).getUTCDay(); // 0 = domingo
      const de = somar(HOJE, -((semana + 6) % 7)); // segunda-feira
      const ate = somar(de, 6);
      return { de, ate, rotulo: `Esta semana (${dataBr(de)} a ${dataBr(ate)})` };
    }
    case 'mes':
    case 'mes_passado': {
      // Meses de calendário (Fase 1, F1-03): do dia 1 ao último dia do mês.
      let [a, m] = [Number(HOJE.slice(0, 4)), Number(HOJE.slice(5, 7))];
      if (p.preset === 'mes_passado') [a, m] = m === 1 ? [a - 1, 12] : [a, m - 1];
      const de = `${a}-${String(m).padStart(2, '0')}-01`;
      const ate = somar(m === 12 ? `${a + 1}-01-01` : `${a}-${String(m + 1).padStart(2, '0')}-01`, -1);
      return { de, ate, rotulo: `${p.preset === 'mes' ? 'Este mês' : 'Mês passado'} (${dataBr(de)} a ${dataBr(ate)})` };
    }
    case '30':
    case '90': {
      const n = Number(p.preset);
      const de = somar(HOJE, -(n - 1));
      return { de, ate: HOJE, rotulo: `Últimos ${n} dias (${dataBr(de)} a ${dataBr(HOJE)})` };
    }
    case 'trimestre': {
      const mi = (p.trimestre - 1) * 3 + 1;
      const de = `${p.ano}-${String(mi).padStart(2, '0')}-01`;
      const ate = somar(`${mi + 3 > 12 ? p.ano + 1 : p.ano}-${String(mi + 3 > 12 ? 1 : mi + 3).padStart(2, '0')}-01`, -1);
      return { de, ate, rotulo: `${p.trimestre}º trimestre de ${p.ano} (${dataBr(de)} a ${dataBr(ate)})` };
    }
    case 'ano':
      return { de: `${p.ano}-01-01`, ate: `${p.ano}-12-31`, rotulo: `Ano de ${p.ano} (01/01/${p.ano} a 31/12/${p.ano})` };
    case 'personalizado':
      return { de: p.de, ate: p.ate, rotulo: `Período personalizado (${dataBr(p.de)} a ${dataBr(p.ate)})` };
  }
}

/** A data está no período? Sem data (ex.: oportunidade aberta sem encerramento) fica fora de qualquer período que não seja "Todo o período". */
export function noPeriodo(p: Periodo, data: string | undefined | null, hoje: string): boolean {
  const i = intervalo(p, hoje);
  if (!i.de || !i.ate) return true;
  if (!data) return false;
  return data >= i.de && data <= i.ate;
}
